import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useAuth } from "../../auth";
import { exposureErrorDetail } from "../../services/istio.service";
import RestoreService from "../../services/restore.service";
import {
  IRebuildPlan,
  IRebuildResult,
  IRebuildVerify,
  ISpecAdoptResult,
  ISpecPlan,
  RebuildBucket,
  REBUILD_BUCKET_HINT,
  REBUILD_BUCKET_LABEL,
  SpecBucket,
  SPEC_BUCKET_HINT,
  SPEC_BUCKET_LABEL,
} from "../../types/api/restore.type";

const SPEC_COLOR: Record<SpecBucket, "success" | "error" | "warning" | "info" | "default"> = {
  in_sync: "success",
  drifted: "warning",
  not_recorded: "error",
  invalid_record: "error",
  no_database: "info",
  unknown_backup_account: "error",
  no_accounts_row: "error",
};

const REBUILD_COLOR: Record<RebuildBucket, "success" | "error" | "warning" | "info" | "default"> = {
  healthy: "success",
  missing: "error",
  partial: "warning",
  no_backup: "error",
  no_record: "warning",
  invalid_record: "error",
  restoring: "info",
};

/** Buckets the adopt pass repairs by writing what is live into the record. */
const ADOPTABLE: SpecBucket[] = ["not_recorded", "drifted", "invalid_record"];

/**
 * Render order for the bucket sections. Typed so that leaving a bucket out fails the build:
 * these arrays drive both the chips and the tables, so a missing entry does not degrade — the
 * rows vanish. In a DR where every database is mid-restore, the page said "Nothing to restore."
 */
type ExhaustiveOrder<T extends string> = readonly T[] & { length: 7 };

const SPEC_ORDER: SpecBucket[] = [
  "not_recorded",
  "invalid_record",
  "no_accounts_row",
  "unknown_backup_account",
  "drifted",
  "no_database",
  "in_sync",
];

const REBUILD_ORDER = [
  "missing",
  "invalid_record",
  "restoring",
  "partial",
  "no_backup",
  "no_record",
  "healthy",
] as const satisfies ExhaustiveOrder<RebuildBucket>;

/** `Tenant · Name` over the id, falling back to the id alone when accounts cannot be read. */
const EnvironmentCell = ({
  envId,
  name,
  tenant,
}: {
  envId: number;
  name?: string | null;
  tenant?: string | null;
}) => {
  const label = [tenant, name].filter(Boolean).join(" \u00b7 ");
  return (
    <>
      {label && (
        <Box component="div" sx={{ fontWeight: 500 }}>
          {label}
        </Box>
      )}
      <Box component="div" sx={{ fontSize: 12, opacity: label ? 0.7 : 1 }}>
        {envId}
      </Box>
    </>
  );
};

const summariseAdopt = (result: ISpecAdoptResult) => {
  const done = result.recorded?.length ?? 0;
  const parts = [`Recorded ${done} environment${done === 1 ? "" : "s"}.`];
  if (result.failed?.length) {
    parts.push(
      `${result.failed.length} failed: ${result.failed
        .map((f) => `env ${f.env_id} (${f.reason})`)
        .join("; ")}.`
    );
  }
  if (result.skipped?.length) {
    parts.push(`Skipped: ${result.skipped.map((s) => `env ${s.env_id} ${s.reason}`).join("; ")}.`);
  }
  return parts.join(" ");
};

const summariseRebuild = (result: IRebuildResult) => {
  const done = result.rebuilt?.length ?? 0;
  const parts = [
    `Started ${done} rebuild${done === 1 ? "" : "s"}. Each restores in its own pod — use ` +
      `Verify once it reports Running.`,
  ];
  // The rule engine is deliberately not recreated, and nothing else would say so.
  // Deferred is not a failure and not a success — it is work that has not started yet. Left
  // out, the operator reads "Started 2 rebuilds" for a 32-database recovery and walks away.
  const held = result.deferred?.length ?? 0;
  if (held) {
    parts.push(
      `${held} more ${held === 1 ? "is" : "are"} waiting for a slot and will start as the ` +
        `running restores finish.`
    );
  }
  const notes = (result.rebuilt ?? []).filter((r) => r.note);
  if (notes.length) {
    parts.push(notes.map((r) => `env ${r.env_id}: ${r.note}`).join("; ") + ".");
  }
  if (result.failed?.length) {
    parts.push(
      `${result.failed.length} failed: ${result.failed
        .map((f) => `env ${f.env_id} (${f.reason})`)
        .join("; ")}.`
    );
  }
  if (result.skipped?.length) {
    parts.push(`Skipped: ${result.skipped.map((s) => `env ${s.env_id} ${s.reason}`).join("; ")}.`);
  }
  return parts.join(" ");
};

export const EnvironmentRestore = () => {
  // Recorded by the server for the audit log: these act across every tenant, so there is no
  // owner to check against. Without an identity the buttons are disabled rather than
  // attributing a recovery to a placeholder.
  const { user } = useAuth();
  const actor: string | null = user?.email || user?.name || null;

  const [specPlan, setSpecPlan] = useState<ISpecPlan | null>(null);
  const [rebuildPlan, setRebuildPlan] = useState<IRebuildPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [confirmAdopt, setConfirmAdopt] = useState(false);
  const [confirmRebuild, setConfirmRebuild] = useState<number[] | "all" | null>(null);
  const [verified, setVerified] = useState<Record<number, IRebuildVerify>>({});
  // setBusy does not take effect until the next render, which is too late to stop a second
  // click in the same tick. The ref is the thing the guard actually reads.
  const busyRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    // A verification describes the moment it ran. Keeping it across a refresh lets a chip read
    // "data is back" for an environment that has since gone missing, or whose restore has only
    // just started — exactly the claim nobody should be trusting during a recovery.
    setVerified({});
    try {
      const [spec, rebuild] = await Promise.all([
        RestoreService.specPlan(),
        RestoreService.rebuildPlan(),
      ]);
      setSpecPlan(spec.data);
      setRebuildPlan(rebuild.data);
      setError(null);
    } catch (e) {
      setError(exposureErrorDetail(e).message || "Could not compute a restore plan");
      setSpecPlan(null);
      setRebuildPlan(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = useCallback(
    async (key: string, action: () => Promise<any>, summarise: (data: any) => string) => {
      // Guarded here as well as on the buttons: a dialog's confirm closes and fires in the same
      // handler, so a double-click can start two overlapping rebuilds of the same databases
      // before any disabled state renders.
      if (busyRef.current) return;
      busyRef.current = key;
      setBusy(key);
      setOutcome(null);
      try {
        const { data } = await action();
        setOutcome(summarise(data));
        setError(null);
        await load();
      } catch (e) {
        setError(exposureErrorDetail(e).message || "The action failed");
      } finally {
        busyRef.current = null;
        setBusy(null);
      }
    },
    [load]
  );

  const keep = useCallback(
    async (envId: number) => {
      if (busyRef.current) return;
      busyRef.current = `keep-${envId}`;
      setBusy(`keep-${envId}`);
      try {
        await RestoreService.keepEnvironment(actor!, envId);
        // The bucket is derived from the record, so the row only moves once the plan is re-read.
        await load();
      } catch (e) {
        setError(exposureErrorDetail(e).message || "Could not clear the delete mark");
      } finally {
        busyRef.current = null;
        setBusy(null);
      }
    },
    [actor, load]
  );

  const verify = useCallback(async (envId: number) => {
    if (busyRef.current) return;
    busyRef.current = `verify-${envId}`;
    setBusy(`verify-${envId}`);
    try {
      const { data } = await RestoreService.verifyRebuild(envId);
      setVerified((prev) => ({ ...prev, [envId]: data }));
      setError(null);
    } catch (e) {
      setError(exposureErrorDetail(e).message || "Could not verify the rebuild");
    } finally {
      busyRef.current = null;
      setBusy(null);
    }
  }, []);

  const specGrouped = useMemo(() => {
    const byBucket = new Map<SpecBucket, ISpecPlan["entries"]>();
    (specPlan?.entries ?? []).forEach((entry) => {
      byBucket.set(entry.bucket, [...(byBucket.get(entry.bucket) ?? []), entry]);
    });
    return SPEC_ORDER.filter((b) => (byBucket.get(b) ?? []).length > 0).map((bucket) => ({
      bucket,
      entries: byBucket.get(bucket) ?? [],
    }));
  }, [specPlan]);

  const rebuildGrouped = useMemo(() => {
    const byBucket = new Map<RebuildBucket, IRebuildPlan["entries"]>();
    (rebuildPlan?.entries ?? []).forEach((entry) => {
      byBucket.set(entry.bucket, [...(byBucket.get(entry.bucket) ?? []), entry]);
    });
    return REBUILD_ORDER.filter((b) => (byBucket.get(b) ?? []).length > 0).map((bucket) => ({
      bucket,
      entries: byBucket.get(bucket) ?? [],
    }));
  }, [rebuildPlan]);

  if (loading && !specPlan && !rebuildPlan) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !specPlan && !rebuildPlan) {
    return (
      <Box p={2}>
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!specPlan || !rebuildPlan) return null;

  const nothingRecorded = specPlan.recorded_count === 0;

  return (
    <Box p={2}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {outcome && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setOutcome(null)}>
          {outcome}
        </Alert>
      )}

      {/* The one state that makes everything else on this tab moot. */}
      {nothingRecorded && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>No environment specs are recorded</strong> — nothing here could be rebuilt
          after a cluster loss, whatever the rebuild plan below says. Use{" "}
          <strong>Record all</strong> to back-fill from what is running.
        </Alert>
      )}

      {!rebuildPlan.enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Rebuild is switched off on this server.</strong> Planning and recording still
          work; applying a rebuild is refused until <code>ENVIRONMENT_REBUILD_ENABLED</code> is
          set, which should follow a recovery drill rather than precede it.
        </Alert>
      )}

      {!actor && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Restore actions are disabled: the server records who ran them, and this session has no
          signed-in identity to attribute them to.
        </Alert>
      )}

      {/* ---------------- Spec records ---------------- */}
      <Typography variant="h6" gutterBottom>
        Spec records
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        What a database would be rebuilt from — its size, tier, node pool, backup schedules and
        which storage account holds its backups. None of that survives the cluster.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
          {SPEC_ORDER.map((bucket) => (
            <Tooltip key={bucket} title={SPEC_BUCKET_HINT[bucket]}>
              <Chip
                size="small"
                color={specPlan.counts[bucket] ? SPEC_COLOR[bucket] : "default"}
                variant={specPlan.counts[bucket] ? "filled" : "outlined"}
                label={`${SPEC_BUCKET_LABEL[bucket]}: ${specPlan.counts[bucket] ?? 0}`}
              />
            </Tooltip>
          ))}
          <Box flexGrow={1} />
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || busy !== null}>
            Refresh
          </Button>
          <Button
            variant="contained"
            disabled={!specPlan.adoptable || busy !== null || !actor}
            onClick={() => setConfirmAdopt(true)}
          >
            Record all ({specPlan.adoptable})
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {specPlan.recorded_count} environment{specPlan.recorded_count === 1 ? "" : "s"} on
          record · recording writes to accounts and touches no Kubernetes object
        </Typography>
      </Paper>

      {specGrouped.map(({ bucket, entries }) => (
        <Box key={bucket} mb={2}>
          <Tooltip title={SPEC_BUCKET_HINT[bucket]}>
            <Typography variant="subtitle2" gutterBottom>
              {SPEC_BUCKET_LABEL[bucket]} ({entries.length})
            </Typography>
          </Tooltip>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Environment</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.env_id} hover>
                    <TableCell>
                      <EnvironmentCell
                        envId={entry.env_id}
                        name={entry.name}
                        tenant={entry.tenant_name}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{entry.detail ?? "—"}</TableCell>
                    <TableCell align="right">
                      {ADOPTABLE.includes(bucket) && (
                        <Button
                          size="small"
                          disabled={busy !== null || !actor}
                          onClick={() =>
                            run(
                              `adopt-${entry.env_id}`,
                              () => RestoreService.specAdopt(actor!, [entry.env_id]),
                              summariseAdopt
                            )
                          }
                        >
                          {busy === `adopt-${entry.env_id}` ? "Recording…" : "Record"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      {/* ---------------- Rebuild ---------------- */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Rebuild
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Recreates a missing database under its original id and restores it from the backup set
        the plan pins. Keeping the id is what makes its backups, its credentials and its
        exposure record line up again.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
          {REBUILD_ORDER.map((bucket) => (
            <Tooltip key={bucket} title={REBUILD_BUCKET_HINT[bucket]}>
              <Chip
                size="small"
                color={rebuildPlan.counts[bucket] ? REBUILD_COLOR[bucket] : "default"}
                variant={rebuildPlan.counts[bucket] ? "filled" : "outlined"}
                label={`${REBUILD_BUCKET_LABEL[bucket]}: ${rebuildPlan.counts[bucket] ?? 0}`}
              />
            </Tooltip>
          ))}
          <Box flexGrow={1} />
          <Tooltip title="Recreates every missing database and starts its restore">
            <span>
              <Button
                variant="contained"
                color="error"
                disabled={
                  !rebuildPlan.actionable ||
                  !rebuildPlan.enabled ||
                  busy !== null ||
                  !actor
                }
                onClick={() => setConfirmRebuild("all")}
              >
                Rebuild all ({rebuildPlan.actionable})
              </Button>
            </span>
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {rebuildPlan.max_concurrency} restores at a time, cluster-wide · only “Missing” is
          ever acted on
        </Typography>
      </Paper>

      {rebuildGrouped.length === 0 && <Alert severity="success">Nothing to restore.</Alert>}

      {rebuildGrouped.map(({ bucket, entries }) => (
        <Box key={bucket} mb={2}>
          <Tooltip title={REBUILD_BUCKET_HINT[bucket]}>
            <Typography variant="subtitle2" gutterBottom>
              {REBUILD_BUCKET_LABEL[bucket]} ({entries.length})
            </Typography>
          </Tooltip>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Environment</TableCell>
                  <TableCell>Backup set</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const check = verified[entry.env_id];
                  return (
                    <TableRow key={entry.env_id} hover>
                      <TableCell>
                        <EnvironmentCell
                          envId={entry.env_id}
                          name={entry.name}
                          tenant={entry.tenant_name}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{entry.backup_set ?? "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {entry.detail ?? "—"}
                        {check && (
                          <Box component="div" sx={{ mt: 0.5 }}>
                            {/* A chip is a label, not a paragraph. The verdict goes in the chip
                                and the reason underneath as ordinary wrapping text -- a verify
                                reason runs to a couple of sentences and a chip cannot hold one
                                without forcing the whole table to scroll sideways. */}
                            <Chip
                              size="small"
                              color={check.verified ? "success" : "warning"}
                              label={check.verified ? "Verified" : "Not verified"}
                            />
                            {check.checks?.restore_flag_set && (
                              <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ ml: 0.5 }}
                                label="Still armed"
                                title={
                                  "DB_BACKUP_RESTORE_ENABLED is still true, so a pod replacement " +
                                  "that loses the completion marker would restore again. " +
                                  "Turning it off restarts the database, so do it in a " +
                                  "maintenance window."
                                }
                              />
                            )}
                            <Box
                              component="div"
                              sx={{ mt: 0.5, fontSize: 12, opacity: 0.85, whiteSpace: "normal" }}
                            >
                              {check.verified
                                ? "The data came back: the restore completed, the pod is live, and " +
                                  "the cluster's system identifier matches the backup it was restored from."
                                : check.detail ||
                                  `Failed checks: ${
                                    Object.entries(check.checks)
                                      // restore_flag_set is a *state*, not a passed check:
                                      // listing it here put "the flag is off" in a list of
                                      // failures, which reads exactly backwards.
                                      .filter(([name, ok]) => !ok && name !== "restore_flag_set")
                                      .map(([name]) => name)
                                      .join(", ") || "unknown"
                                  }`}
                            </Box>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {bucket === "missing" && (
                          <Button
                            size="small"
                            disabled={busy !== null || !actor || !rebuildPlan.enabled}
                            onClick={() => setConfirmRebuild([entry.env_id])}
                          >
                            Rebuild
                          </Button>
                        )}
                        {bucket === "partial" &&
                          entry.detail?.includes("delete was started") && (
                            // The plan tells the operator to use Keep; without this there was
                            // nothing to press, and the stamp was a one-way door.
                            <Button
                              size="small"
                              color="warning"
                              disabled={busy !== null || !actor}
                              onClick={() => keep(entry.env_id)}
                            >
                              {busy === `keep-${entry.env_id}` ? "Keeping…" : "Keep"}
                            </Button>
                          )}
                        {(bucket === "healthy" ||
                          bucket === "partial" ||
                          bucket === "restoring") && (
                          <Button
                            size="small"
                            disabled={busy !== null}
                            onClick={() => verify(entry.env_id)}
                          >
                            {busy === `verify-${entry.env_id}` ? "Checking…" : "Verify"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      <Dialog open={confirmAdopt} onClose={() => setConfirmAdopt(false)}>
        <DialogTitle>Record {specPlan.adoptable} environment spec(s)?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This reads each running database and writes what it finds into the environment's
            record in accounts. No Kubernetes object is created, changed or deleted.
            <Box component="p" sx={{ mt: 2 }}>
              Two fields are read back rather than stored verbatim — the tier comes from a label
              and the resource mode from comparing requests against limits — so check anything
              unusual afterwards.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAdopt(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy !== null}
            onClick={() => {
              setConfirmAdopt(false);
              run("adopt-all", () => RestoreService.specAdopt(actor!), summariseAdopt);
            }}
          >
            Record them
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRebuild !== null} onClose={() => setConfirmRebuild(null)}>
        <DialogTitle>
          {confirmRebuild === "all"
            ? `Rebuild ${rebuildPlan.actionable} database(s)?`
            : (() => {
                // Named, not just numbered: the confirm dialog is the last chance to notice
                // that the id about to be rebuilt is not the one you meant.
                const id = (confirmRebuild as number[])?.[0];
                const row = rebuildPlan.entries.find((e) => e.env_id === id);
                const label = [row?.tenant_name, row?.name].filter(Boolean).join(" \u00b7 ");
                return `Rebuild ${label ? `${label} (${id})` : `environment ${id}`}?`;
              })()}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This creates the database in Kubernetes under its original id and restores it from
            the pinned backup set. It is the real recovery action, not a dry run.
            {confirmRebuild === "all" && (
              // The safety rule is to read the ids before confirming, and a count cannot be
              // read. Every environment about to be rebuilt is listed, named and numbered.
              <Box
                component="ul"
                sx={{
                  mt: 2, mb: 0, pl: 3, maxHeight: 220, overflowY: "auto",
                  fontSize: 13, lineHeight: 1.7,
                }}
              >
                {rebuildPlan.entries
                  .filter((e) => e.bucket === "missing")
                  .map((e) => {
                    const label = [e.tenant_name, e.name].filter(Boolean).join(" \u00b7 ");
                    return (
                      <Box component="li" key={e.env_id}>
                        <strong>{e.env_id}</strong>
                        {label ? ` \u2014 ${label}` : ""}
                        {e.backup_set ? ` \u2014 from ${e.backup_set}` : ""}
                      </Box>
                    );
                  })}
              </Box>
            )}
            <Box component="p" sx={{ mt: 2 }}>
              <strong>What it does not do.</strong> It does not recreate a rule engine — that
              one's credentials were never recorded — and it does not claim a gateway port; the
              Recovery tab rebuilds exposures separately.
            </Box>
            <Box component="p" sx={{ mt: 2 }}>
              At most {rebuildPlan.max_concurrency} restores run at once, counted across the
              whole cluster — anything over that is held back and starts as slots free up, so a
              large recovery finishes over several rounds rather than all at once. Restores
              continue in their own pods after this returns. Use <strong>Verify</strong> on each
              once it reports Running — a pod that came back is not the same as data that came
              back.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRebuild(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy !== null}
            onClick={() => {
              const ids = confirmRebuild === "all" ? undefined : (confirmRebuild as number[]);
              setConfirmRebuild(null);
              run(
                ids ? `rebuild-${ids[0]}` : "rebuild-all",
                () => RestoreService.rebuild(actor!, ids),
                summariseRebuild
              );
            }}
          >
            Rebuild
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnvironmentRestore;
